import type { AgentProjectPickAction } from './agentShortcut';

export type AgentProjectPickMeta = {
  title: string;
  description: string;
  submitLabel: string;
  userMessage: (projectLabel: string) => string;
};

export const AGENT_PROJECT_PICK_META: Record<AgentProjectPickAction, AgentProjectPickMeta> = {
  apply_ordering: {
    title: 'Apply suggested ordering',
    description: 'Collections and requests will be ordered so login-style calls tend to run first.',
    submitLabel: 'Apply',
    userMessage: (projectLabel) => `Apply suggested ordering for project "${projectLabel}"`,
  },
  apply_default_headers: {
    title: 'Apply default headers',
    description:
      'Adds Content-Type and Accept: application/json to requests with a body. Leave Collection and Request as All Collections / All Requests to apply across the project.',
    submitLabel: 'Apply',
    userMessage: (projectLabel) =>
      `Apply default headers for project "${projectLabel}" (all collections, all requests)`,
  },
  apply_bearer: {
    title: 'Apply Bearer token to requests',
    description:
      'Sets Authorization: Bearer on requests using the token variable from your environment (e.g. {{token}}). Leave Collection and Request as All Collections / All Requests to apply across the project.',
    submitLabel: 'Apply',
    userMessage: (projectLabel) =>
      `Apply Bearer token to requests in project "${projectLabel}" (all collections, all requests)`,
  },
  suggest_datasets: {
    title: 'Generate suggested assertions',
    description:
      'Choose a project and optionally narrow to a collection or request. Leave Collection and Request as All Collections / All Requests to process the whole project.',
    submitLabel: 'Run',
    userMessage: (projectLabel) => `Generate suggested assertions for "${projectLabel}"`,
  },
  guide_run: {
    title: 'Guide run (Collections)',
    description: 'Opens Collections with the first collection and request for guided execution.',
    submitLabel: 'Open',
    userMessage: (projectLabel) => `Guide run (Collections) for project "${projectLabel}"`,
  },
  run_request_by_request: {
    title: 'Run request by request',
    description: 'Runs the selected collection one request at a time.',
    submitLabel: 'Run',
    userMessage: (projectLabel) => `Run request by request for project "${projectLabel}"`,
  },
  run_round_robin: {
    title: 'Round robin run',
    description: 'Runs requests in round-robin order across collections.',
    submitLabel: 'Run',
    userMessage: (projectLabel) => `Round robin run for project "${projectLabel}"`,
  },
  run_full_flow: {
    title: 'Run full flow (all requests)',
    description: 'Runs all requests in the project flow.',
    submitLabel: 'Run',
    userMessage: (projectLabel) => `Run full flow for project "${projectLabel}"`,
  },
  run_collection: {
    title: 'Open Collections (from agent)',
    description: 'Opens the Collections page for the selected project.',
    submitLabel: 'Open',
    userMessage: (projectLabel) => `Open Collections for project "${projectLabel}"`,
  },
  test_cycles: {
    title: 'Test Cycles (from agent)',
    description: 'Opens Test Plans / Cycles for the selected project.',
    submitLabel: 'Open',
    userMessage: (projectLabel) => `Open Test Cycles for project "${projectLabel}"`,
  },
};

/** Project-pick actions that run inline in the widget after choosing a project. */
export const WIDGET_INLINE_PROJECT_PICK_ACTIONS = new Set<AgentProjectPickAction>([
  'apply_ordering',
  'apply_default_headers',
  'apply_bearer',
  'suggest_datasets',
]);

export function isWidgetInlineProjectPickAction(
  action: string,
): action is AgentProjectPickAction {
  return WIDGET_INLINE_PROJECT_PICK_ACTIONS.has(action as AgentProjectPickAction);
}
